// Nudges mobile users to add the site to their home screen — but only once,
// and not before their second visit (first visit already gets the welcome
// message). "Second visit" = the next time they're seen at least 12h after
// the last time we saw them, tracked via localStorage across page loads.

import { useEffect, useState } from "react";
import { Button } from "./Button";

const LAST_SEEN_KEY = "cd_last_seen_at";
const PROMPT_SHOWN_KEY = "cd_pwa_prompt_shown";
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isMobile(): boolean {
  return isIOS() || /Android/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function PwaInstallPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const now = Date.now();
    const lastSeenRaw = localStorage.getItem(LAST_SEEN_KEY);
    const alreadyShown = localStorage.getItem(PROMPT_SHOWN_KEY) === "1";

    if (!alreadyShown && isMobile() && !isStandalone() && lastSeenRaw) {
      const lastSeen = Number(lastSeenRaw);
      if (Number.isFinite(lastSeen) && now - lastSeen >= TWELVE_HOURS_MS) {
        setShow(true);
        localStorage.setItem(PROMPT_SHOWN_KEY, "1");
      }
    }
    localStorage.setItem(LAST_SEEN_KEY, String(now));
  }, []);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setShow(false); }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper p-5 text-center shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <span className="text-4xl" aria-hidden>🍊</span>
        <h2 className="mt-2 text-lg font-bold text-ink">Add Citrus-Dala to your home screen</h2>
        <p className="mt-2 text-sm text-ink-soft">
          {isIOS() ? (
            <>Tap the <strong>Share</strong> icon, then <strong>"Add to Home Screen."</strong></>
          ) : (
            <>Tap your browser's <strong>⋮ menu</strong>, then <strong>"Add to Home screen"</strong> or <strong>"Install app."</strong></>
          )}
        </p>
        <p className="mt-2 text-xs text-ink-muted">It opens like a real app — no browser bar, one tap away.</p>
        <Button variant="ghost" onClick={() => setShow(false)} className="mt-4 w-full">
          Got it
        </Button>
      </div>
    </div>
  );
}
